import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  Upload,
  FileCheck2,
  Lock,
  Shield,
  Mail,
  BadgeCheck,
  Check,
} from 'lucide-react';
import './formRegister.css';
import { loadGoogleMapsPlaces } from '../../utils/loadGoogleMapsScript';

// 🔧 Base URL normalisée (enlève les / finaux et un /api éventuel)
const RAW = process.env.REACT_APP_API_URL || '';
const API_BASE = RAW.replace(/\/+$/, '').replace(/\/api$/i, ''); // ex: https://backend-ft0c.onrender.com

// Petite popup OTP (réutilisable)
function VerifyPhonePopup({
  open,
  phone,
  loading,
  error,
  onCancel,
  onResend,
  onValidate,
  variant = 'web',
}) {
  const [code, setCode] = useState('');

  const COOLDOWN = 30;
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!open) return;
    setCooldown(COOLDOWN);
    setCode('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (cooldown <= 0) return;
    const id = setInterval(() => {
      setCooldown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [open, cooldown]);

  const handleResendClick = async () => {
    if (cooldown > 0) return;
    await onResend?.();
    setCooldown(COOLDOWN);
  };

  if (!open) return null;

  return (
    <div
      className={`otp-popup ${variant === 'app' ? 'app' : 'web'}`}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        backdropFilter: 'blur(4px)',
      }}
    >
      <div className={`otp-card ${variant === 'app' ? 'app' : 'web'}`}>
        <button type="button" onClick={onCancel} aria-label="Fermer" className="otp-close">
          ×
        </button>

        <h3 className="otp-card__title">Vérification du numéro</h3>
        <p className="otp-card__lead">
          Un code SMS a été envoyé au <strong>{phone}</strong>.
        </p>

        <input
          className="otp-card__input"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="Code à 6 chiffres"
          inputMode="numeric"
          maxLength={6}
        />

        {error ? <div className="otp-card__error">{error}</div> : null}

        <div className="otp-card__actions">
          <button
            type="button"
            className="otp-card__btn otp-card__btn--primary"
            onClick={() => onValidate(code)}
            disabled={loading || code.length !== 6}
          >
            Valider
          </button>
          <button
            type="button"
            className="otp-card__btn otp-card__btn--secondary"
            onClick={handleResendClick}
            disabled={loading || cooldown > 0}
            title={cooldown > 0 ? `Vous pourrez renvoyer dans ${cooldown}s` : 'Renvoyer le code'}
          >
            {cooldown > 0 ? `Renvoyer (${cooldown}s)` : 'Renvoyer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DocUploadField({ id, label, hint, file, onChange, error }) {
  return (
    <div className="doc-upload">
      <label className={`doc-upload__zone${file ? ' doc-upload__zone--has-file' : ''}`} htmlFor={id}>
        <input
          id={id}
          className="doc-upload__input"
          type="file"
          accept=".pdf,image/png,image/jpeg"
          onChange={onChange}
        />
        <span className="doc-upload__icon" aria-hidden>
          <Upload size={22} strokeWidth={1.75} />
        </span>
        <span className="doc-upload__body">
          <span className="doc-upload__title">{label}</span>
          <span className="doc-upload__meta">{hint}</span>
          {file ? (
            <span className="doc-upload__filename">
              <FileCheck2 size={15} strokeWidth={2} aria-hidden />
              <span className="doc-upload__name">{file.name}</span>
            </span>
          ) : (
            <span className="doc-upload__cta">Choisir un fichier</span>
          )}
        </span>
      </label>
      {error ? <p className="error-message">{error}</p> : null}
    </div>
  );
}

function FormRegister({ title, isPro = false, isApp = false, subtitle }) {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    adresse: '',
    ville: '',
    email: '',
    password: '',
    confirmPassword: '',
    telephone: '',
    siret: '',
    role: isPro ? 'pro' : 'user',
  });

  const [docs, setDocs] = useState({
    kbis: null,
    id: null,
    proofAddress: null,
  });

  const [msg, setMsg] = useState({ text: '', type: '' });
  const [errors, setErrors] = useState({});

  const [otpOpen, setOtpOpen] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpPhone, setOtpPhone] = useState('');

  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);

  const [googleReady, setGoogleReady] = useState(false);
  const [addressPredictions, setAddressPredictions] = useState([]);
  const [addressOpen, setAddressOpen] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressBoxRef = useRef(null);

  const emailRegex = /^[\w.-]+@[\w.-]+\.[a-zA-Z]{2,}$/;
  const phoneRegex = /^0\d{9}$/;
  const passwordComplexityRegex =
    /^(?=.*[A-Z])(?=.*\d)(?=.*[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]).{6,20}$/;

  const validateFile = (file) => {
    const MAX_SIZE = 10 * 1024 * 1024;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!file) return 'Fichier requis.';
    if (file.size > MAX_SIZE) return 'Taille max 10MB.';
    if (!allowed.includes(file.type)) return 'Formats autorisés: PDF/JPG/PNG.';
    return '';
  };

  useEffect(() => {
    let ignore = false;
    loadGoogleMapsPlaces()
      .then(() => {
        if (!ignore) setGoogleReady(true);
      })
      .catch(() => {
        if (!ignore) setGoogleReady(false);
      });
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (!addressBoxRef.current) return;
      if (!addressBoxRef.current.contains(e.target)) {
        setAddressOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const debouncedAdresse = useMemo(() => {
    let t = null;
    return (value, cb) => {
      if (t) clearTimeout(t);
      t = setTimeout(() => cb(value), 220);
    };
  }, []);

  const fetchPredictions = (input) => {
    if (!googleReady || !input || input.trim().length < 3) {
      setAddressPredictions([]);
      return;
    }
    setAddressLoading(true);

    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: 'fr' },
        types: ['address'],
      },
      (preds, status) => {
        setAddressLoading(false);
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !preds) {
          setAddressPredictions([]);
          return;
        }
        setAddressPredictions(preds.slice(0, 6));
      }
    );
  };

  const handleAdresseChange = (e) => {
    const v = e.target.value;
    setFormData((prev) => ({ ...prev, adresse: v }));
    setErrors((prev) => ({ ...prev, adresse: '' }));
    setAddressOpen(true);

    debouncedAdresse(v, (val) => fetchPredictions(val));
  };

  const pickAddress = (prediction) => {
    const description = prediction.description || '';
    setFormData((prev) => ({ ...prev, adresse: description }));
    setAddressPredictions([]);
    setAddressOpen(false);

    if (!googleReady || !prediction.place_id) return;
    const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
    placesService.getDetails(
      { placeId: prediction.place_id, fields: ['address_components'] },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK) return;
        const comps = place?.address_components || [];
        const city =
          comps.find((c) => c.types?.includes('locality'))?.long_name ||
          comps.find((c) => c.types?.includes('postal_town'))?.long_name ||
          comps.find((c) => c.types?.includes('administrative_area_level_2'))?.long_name ||
          '';
        if (city) {
          setFormData((prev) => ({ ...prev, ville: prev.ville || city }));
          setErrors((prev) => ({ ...prev, ville: '' }));
        }
      }
    );
  };

  const validateForm = () => {
    const newErrors = {
      email: !emailRegex.test(formData.email) ? 'Adresse email invalide.' : '',
      password:
        formData.password.length < 6 || formData.password.length > 20
          ? 'Le mot de passe doit contenir entre 6 et 20 caractères.'
          : !passwordComplexityRegex.test(formData.password)
          ? 'Le mot de passe doit inclure au moins 1 majuscule, 1 chiffre et 1 caractère spécial.'
          : '',
      confirmPassword:
        formData.confirmPassword !== formData.password ? 'Les mots de passe ne correspondent pas.' : '',
      telephone: !phoneRegex.test(formData.telephone)
        ? 'Le numéro doit contenir 10 chiffres et commencer par 0.'
        : '',
      adresse:
        formData.adresse.trim().length < 5
          ? "L'adresse doit contenir au moins 5 caractères."
          : '',
      ville: formData.ville.trim().length < 2 ? 'La ville est obligatoire.' : '',
      terms: acceptedTerms ? '' : 'Vous devez accepter les conditions pour continuer.',
    };

    if (isPro) {
      newErrors.siret = !/^\d{14}$/.test(formData.siret) ? 'SIRET invalide (14 chiffres).' : '';
      newErrors.doc_kbis = validateFile(docs.kbis);
      newErrors.doc_id = validateFile(docs.id);
      newErrors.doc_proofAddress = validateFile(docs.proofAddress);
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleFileChange = (key) => (e) => {
    const file = e.target.files?.[0] || null;
    setDocs((prev) => ({ ...prev, [key]: file }));
    const errKey = key === 'proofAddress' ? 'doc_proofAddress' : `doc_${key}`;
    setErrors((prev) => ({ ...prev, [errKey]: '' }));
  };

  const sendOtp = async (telephone) => {
    try {
      setOtpLoading(true);
      setOtpError('');
      const res = await fetch(`${API_BASE}/api/otp/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ telephone }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Envoi du code impossible');
      setOtpPhone(telephone);
      setOtpOpen(true);
      return true;
    } catch (err) {
      setOtpError(err.message || 'Erreur lors de l’envoi du code');
      return false;
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async (telephone, code) => {
    try {
      setOtpLoading(true);
      setOtpError('');
      const res = await fetch(`${API_BASE}/api/otp/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ telephone, code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.message !== 'Numéro vérifié avec succès.')
        throw new Error(data?.error || 'Code incorrect.');
      return true;
    } catch (err) {
      setOtpError(err.message || 'Vérification impossible');
      return false;
    } finally {
      setOtpLoading(false);
    }
  };

  const resendEmailCode = async (email) => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/resend-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        try {
          const data = await res.json();
          throw new Error(data?.error || 'Impossible de renvoyer le code.');
        } catch {
          if (res.status < 200 || res.status >= 300) throw new Error('Impossible de renvoyer le code.');
        }
      }
      return true;
    } catch {
      return false;
    }
  };

  const registerNow = async () => {
    try {
      const url = `${API_BASE}/api/auth/register`;
      const options = { method: 'POST', credentials: 'include' };

      if (isPro) {
        const fd = new FormData();
        Object.entries(formData).forEach(([k, v]) => {
          if (k === 'confirmPassword') return;
          fd.append(k, v);
        });
        fd.append('acceptedTerms', acceptedTerms ? 'true' : 'false');
        if (docs.kbis) fd.append('kbis', docs.kbis, docs.kbis.name);
        if (docs.id) fd.append('idCard', docs.id, docs.id.name);
        if (docs.proofAddress) fd.append('proofAddress', docs.proofAddress, docs.proofAddress.name);
        options.body = fd;
      } else {
        const payload = { ...formData, acceptedTerms };
        delete payload.confirmPassword;
        options.headers = { 'Content-Type': 'application/json' };
        options.body = JSON.stringify(payload);
      }

      const response = await fetch(url, options);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Une erreur est survenue');

      const recipient = String(data.emailRecipient || formData.email || "").trim().toLowerCase();

      const emailOk = data.emailSent !== false;
      setMsg({
        text: emailOk
          ? `✅ ${data.message} Un code a été envoyé à ${recipient || "votre adresse e-mail"}. ${
              isPro ? "Votre compte pro sera vérifié sous peu." : ""
            }`
          : `✅ ${data.message} ${
              data.emailWarning ||
              "L’email de vérification n’a pas pu être envoyé. Vérifie RESEND_API_KEY / RESEND_FROM sur le serveur ou contacte le support."
            }`,
        type: emailOk ? "success" : "error",
      });

      if (emailOk) {
        setTimeout(() => {
          navigate("/verification", { state: { email: recipient, startCooldown: true } });
        }, 1200);
      }
    } catch (error) {
      setMsg({ text: `❌ Erreur : ${error.message}`, type: 'error' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setMsg({ text: '❌ Veuillez corriger les erreurs dans le formulaire.', type: 'error' });
      return;
    }

    try {
      const phoneCheck = await fetch(`${API_BASE}/api/auth/check-phone/${formData.telephone}`, {
        credentials: 'include',
      });
      const phoneCheckData = await phoneCheck.json();
      if (phoneCheckData.exists) {
        setMsg({ text: '❌ Ce numéro de téléphone est déjà utilisé.', type: 'error' });
        return;
      }

      if (isPro) {
        if (formData.siret) {
          const siretCheck = await fetch(`${API_BASE}/api/auth/check-siret/${formData.siret}`, {
            credentials: 'include',
          });
          const siretCheckData = await siretCheck.json();
          if (siretCheckData.exists) {
            setMsg({ text: '❌ Ce numéro de SIRET est déjà utilisé.', type: 'error' });
            return;
          }
        }
        await registerNow();
        return;
      }

      const okSent = await sendOtp(formData.telephone);
      if (!okSent) {
        setMsg({ text: '❌ Envoi du code SMS impossible.', type: 'error' });
        return;
      }
    } catch (error) {
      setMsg({ text: `❌ Erreur : ${error.message}`, type: 'error' });
    }
  };

  const wrapperClass = `subscribe${isApp ? ' subscribe--app' : ''}`;
  const containerClass = `subscribe_container ${
    isPro ? 'subscribe_container-pro' : 'subscribe_container-user'
  }${isApp ? ' subscribe_container-app' : ''}`;

  const resolvedSubtitle =
    subtitle ??
    (isPro
      ? 'Après envoi de votre dossier (Kbis, identité), notre équipe valide votre compte sous peu.'
      : 'Vous recevrez un code par SMS puis un lien pour confirmer votre email.');

  return (
    <div className={wrapperClass}>
      <div className="register_header">
        <h2 className="register_title">{title}</h2>
        <p className="register_subtitle">{resolvedSubtitle}</p>
        {!isApp && (
          <div className={`register_trust${isPro ? ' register_trust--pro' : ''}`} role="list">
            {isPro ? (
              <>
                <span className="register_trust__item" role="listitem">
                  <Lock size={15} strokeWidth={2} aria-hidden />
                  Échange sécurisé
                </span>
                <span className="register_trust__item" role="listitem">
                  <FileCheck2 size={15} strokeWidth={2} aria-hidden />
                  Vérification des documents
                </span>
                <span className="register_trust__item" role="listitem">
                  <BadgeCheck size={15} strokeWidth={2} aria-hidden />
                  Compte pro identifié
                </span>
              </>
            ) : (
              <>
                <span className="register_trust__item" role="listitem">
                  <Lock size={15} strokeWidth={2} aria-hidden />
                  Données protégées
                </span>
                <span className="register_trust__item" role="listitem">
                  <Mail size={15} strokeWidth={2} aria-hidden />
                  Validation par email
                </span>
                <span className="register_trust__item" role="listitem">
                  <Shield size={15} strokeWidth={2} aria-hidden />
                  Respect de vos données
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <div className={`register_layout ${isPro ? 'register_layout--pro' : 'register_layout--user'}`}>
        {isPro && (
          <aside className="register_pro_pitch">
            <div className="register_pro_pitch-badge">ProMeetService Pro</div>

            <h3 className="register_pro_pitch-title">
              Développez votre activité avec une inscription professionnelle pensée pour les métiers auto.
            </h3>

            <p className="register_pro_pitch-text">
              Rejoignez une plateforme conçue pour les professionnels de l’automobile afin de mieux
              gérer vos rendez-vous, présenter vos services et gagner en visibilité.
            </p>

            <div className="register_pro_pitch-points">
              <div className="register_pro_pitch-point">
                <span className="register_pro_pitch-icon" aria-hidden>
                  <Check size={16} strokeWidth={2.5} />
                </span>
                <div>
                  <strong>Plus de visibilité</strong>
                  <p>Présentez votre activité et soyez trouvé plus facilement par les clients.</p>
                </div>
              </div>

              <div className="register_pro_pitch-point">
                <span className="register_pro_pitch-icon" aria-hidden>
                  <Check size={16} strokeWidth={2.5} />
                </span>
                <div>
                  <strong>Gestion simplifiée</strong>
                  <p>Centralisez vos rendez-vous, informations clients et documents au même endroit.</p>
                </div>
              </div>

              <div className="register_pro_pitch-point">
                <span className="register_pro_pitch-icon" aria-hidden>
                  <Check size={16} strokeWidth={2.5} />
                </span>
                <div>
                  <strong>Image plus professionnelle</strong>
                  <p>Donnez une meilleure expérience à vos clients avec un espace structuré et moderne.</p>
                </div>
              </div>
            </div>

            <div className="register_pro_pitch-footer">
              Idéal pour garages, detailers, pare-brise, lavage auto, mécanique et services spécialisés.
            </div>
          </aside>
        )}

        <form
          className={containerClass}
          onSubmit={handleSubmit}
          encType={isPro ? 'multipart/form-data' : undefined}
        >
          <div className="subscribe_container-forms">
            <input className="input_form" name="nom" placeholder="Nom" onChange={handleChange} />
            <input className="input_form" name="prenom" placeholder="Prénom" onChange={handleChange} />

            <div ref={addressBoxRef} className="address-field">
              <input
                className={`input_form ${errors.adresse ? 'input-error-adresse' : ''}`}
                name="adresse"
                placeholder="Adresse"
                value={formData.adresse}
                onChange={handleAdresseChange}
                onFocus={() => {
                  if (addressPredictions.length) setAddressOpen(true);
                }}
                autoComplete="street-address"
              />
              {errors.adresse && <p className="error-message">{errors.adresse}</p>}

              {googleReady && addressOpen && (addressLoading || addressPredictions.length > 0) && (
                <div className="address-suggest">
                  {addressLoading && <div className="address-suggest__status">Recherche…</div>}

                  {!addressLoading &&
                    addressPredictions.map((p) => (
                      <button
                        key={p.place_id}
                        type="button"
                        className="address-suggest__item"
                        onClick={() => pickAddress(p)}
                      >
                        {p.description}
                      </button>
                    ))}

                  {!addressLoading && addressPredictions.length === 0 && (
                    <div className="address-suggest__status">Aucune suggestion</div>
                  )}
                </div>
              )}
            </div>

            <input
              className={`input_form ${errors.ville ? 'input-error-ville' : ''}`}
              name="ville"
              placeholder="Ville"
              value={formData.ville}
              onChange={handleChange}
              autoComplete="address-level2"
            />
            {errors.ville && <p className="error-message">{errors.ville}</p>}

            <input
              className={`input_form ${errors.email ? 'input-error-email' : ''}`}
              name="email"
              type="email"
              placeholder="Email"
              onChange={handleChange}
              value={formData.email}
            />
            {errors.email && <p className="error-message">{errors.email}</p>}

            <div className="field-wrap field-wrap--password">
              <input
                className={`input_form ${errors.password ? 'input-error-password' : ''}`}
                name="password"
                type={showPwd ? 'text' : 'password'}
                placeholder="Mot de passe"
                onChange={handleChange}
                value={formData.password}
                autoComplete="new-password"
                minLength={6}
                maxLength={20}
              />
              <button
                type="button"
                className="pwd-toggle"
                onClick={() => setShowPwd((v) => !v)}
                aria-label={showPwd ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                title={showPwd ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPwd ? <EyeOff size={18} strokeWidth={2} /> : <Eye size={18} strokeWidth={2} />}
              </button>
            </div>
            {errors.password && <p className="error-message">{errors.password}</p>}

            <div className="field-wrap field-wrap--password">
              <input
                className={`input_form ${errors.confirmPassword ? 'input-error-password' : ''}`}
                name="confirmPassword"
                type={showPwd2 ? 'text' : 'password'}
                placeholder="Confirmer le mot de passe"
                onChange={handleChange}
                value={formData.confirmPassword}
                autoComplete="new-password"
                minLength={6}
                maxLength={20}
              />
              <button
                type="button"
                className="pwd-toggle"
                onClick={() => setShowPwd2((v) => !v)}
                aria-label={showPwd2 ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                title={showPwd2 ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPwd2 ? <EyeOff size={18} strokeWidth={2} /> : <Eye size={18} strokeWidth={2} />}
              </button>
            </div>
            {errors.confirmPassword && <p className="error-message">{errors.confirmPassword}</p>}

            <input
              className={`input_form ${errors.telephone ? 'input-error-telephone' : ''}`}
              name="telephone"
              placeholder="Téléphone"
              inputMode="tel"
              onChange={handleChange}
              value={formData.telephone}
            />
            {errors.telephone && <p className="error-message">{errors.telephone}</p>}

            {isPro && (
              <>
                <input
                  className={`input_form ${errors.siret ? 'input-error-siret' : ''}`}
                  name="siret"
                  placeholder="Numéro de SIRET (14 chiffres)"
                  onChange={handleChange}
                  value={formData.siret}
                />
                {errors.siret && <p className="error-message">{errors.siret}</p>}

                <DocUploadField
                  id="doc-kbis"
                  label="Extrait Kbis / RNE"
                  hint="Document de moins de 3 mois · PDF, JPG ou PNG · max 10 Mo"
                  file={docs.kbis}
                  onChange={handleFileChange('kbis')}
                  error={errors.doc_kbis}
                />

                <DocUploadField
                  id="doc-id"
                  label="Pièce d’identité du représentant légal"
                  hint="CNI ou passeport · PDF, JPG ou PNG · max 10 Mo"
                  file={docs.id}
                  onChange={handleFileChange('id')}
                  error={errors.doc_id}
                />

                <DocUploadField
                  id="doc-address"
                  label="Justificatif d’adresse professionnelle"
                  hint="Facture ou quittance de moins de 3 mois · PDF, JPG ou PNG · max 10 Mo"
                  file={docs.proofAddress}
                  onChange={handleFileChange('proofAddress')}
                  error={errors.doc_proofAddress}
                />
              </>
            )}

            <div className="terms-block">
              <label className="terms-block__label" htmlFor="accept-terms">
                <input
                  id="accept-terms"
                  type="checkbox"
                  className="terms-block__input"
                  checked={acceptedTerms}
                  onChange={(e) => {
                    setAcceptedTerms(e.target.checked);
                    setErrors((prev) => ({ ...prev, terms: '' }));
                  }}
                />
                <span className="terms-block__box" aria-hidden />
                <span className="terms-block__text">
                  J’ai lu et j’accepte les{' '}
                  <a
                    href="/legal/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="terms-block__link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    conditions d’utilisation et la politique de données
                  </a>
                  .
                </span>
              </label>
            </div>
            {errors.terms && <p className="error-message terms-block__error">{errors.terms}</p>}

            <button className="input_form_send" type="submit" disabled={otpLoading}>
              S'inscrire
            </button>
            {msg.text && <p className={`message ${msg.type}`}>{msg.text}</p>}
          </div>
        </form>
      </div>

      {!isPro && (
        <VerifyPhonePopup
          variant={isApp ? 'app' : 'web'}
          open={otpOpen}
          phone={otpPhone || formData.telephone}
          loading={otpLoading}
          error={otpError}
          onCancel={() => {
            setOtpOpen(false);
            setOtpError('');
          }}
          onResend={async () => {
            await sendOtp(otpPhone || formData.telephone);
          }}
          onValidate={async (code) => {
            const ok = await verifyOtp(otpPhone || formData.telephone, code);
            if (!ok) return;
            setOtpOpen(false);
            await registerNow();
          }}
        />
      )}
    </div>
  );
}

export default FormRegister;